import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { safeGetItem, safeSetItem } from "@/lib/safeStorage";
import { useQuery } from "@tanstack/react-query";

const STORAGE_KEY = "last_community_visit";

/**
 * Punkt „neue Beiträge“ an der Community in der gewohnten Handy-Leiste. Fragt jede Minute.
 * `aktiv = false` schaltet die Abfrage ab: `StudentLayout` tut das unter der neuen Ansicht, deren
 * Hülle einen eigenen Punkt ohne Minutenabfrage zeigt (`labor/neueBeitraege.tsx`, Miko Labs,
 * docs/labs.md „Community“).
 */
export const useUnreadCommunityPosts = (aktiv = true) => {
  const [lastVisit, setLastVisit] = useState<string | null>(() => {
    return safeGetItem(STORAGE_KEY);
  });

  // Fetch count of posts newer than last visit
  const { data: hasUnread = false } = useQuery({
    queryKey: ["unread-community-posts", lastVisit],
    queryFn: async () => {
      if (!lastVisit) {
        // If never visited, check if there are any posts at all
        const { count } = await supabase
          .from("community_posts")
          .select("*", { count: "exact", head: true });
        return (count ?? 0) > 0;
      }

      const { count } = await supabase
        .from("community_posts")
        .select("*", { count: "exact", head: true })
        .gt("created_at", lastVisit);

      return (count ?? 0) > 0;
    },
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refresh every minute
    enabled: aktiv,
  });

  const markAsRead = () => {
    const now = new Date().toISOString();
    safeSetItem(STORAGE_KEY, now);
    setLastVisit(now);
  };

  return { hasUnread, markAsRead };
};
